/**
 * The in-flight load registry for the loading screen (q-asset-readiness-loading-screen,
 * AD-794). Loaders report each load with an ANCHOR saying where in the world it will
 * appear; `readiness.ts` decides which anchors are inside the view. Kept `three`-free
 * (type imports only) so the node/NPC placers, which are three-free, can report to it.
 */

import type { Object3D } from 'three';

/** A world-space point. */
export interface Vec3Like {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** Where a pending load will appear. */
export type PendingAnchor =
  /** Everywhere in view (the ground texture): always counts. */
  | { readonly kind: 'global' }
  /** An axis-aligned world box (a placement, a group of placements). */
  | { readonly kind: 'box'; readonly min: Vec3Like; readonly max: Vec3Like }
  /** A live scene object (an avatar the equipment attaches to), as a sphere of `radius`. */
  | { readonly kind: 'object'; readonly object: Object3D; readonly radius: number };

export const GLOBAL_ANCHOR: PendingAnchor = { kind: 'global' };

/** Pad (m) around placement points: a prop's mesh extends past its origin. */
const POINT_PAD = 2;
/** Extra height (m) above the highest point: a building or tree rises from its seat. */
const POINT_RISE = 8;

/** A box anchor around `points` (y defaults to 0), padded so the meshes fit inside. */
export function pointsAnchor(
  points: readonly { readonly x: number; readonly y?: number; readonly z: number }[],
  pad = POINT_PAD,
): PendingAnchor {
  if (points.length === 0) return GLOBAL_ANCHOR;
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (const p of points) {
    const y = p.y ?? 0;
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
    minZ = Math.min(minZ, p.z);
    maxZ = Math.max(maxZ, p.z);
  }
  return {
    kind: 'box',
    min: { x: minX - pad, y: minY - pad, z: minZ - pad },
    max: { x: maxX + pad, y: maxY + POINT_RISE, z: maxZ + pad },
  };
}

/** An object anchor (a sphere of `radius` around the object's world position). */
export function objectAnchor(object: Object3D, radius = 1.5): PendingAnchor {
  return { kind: 'object', object, radius };
}

/** What a loader needs: report a load and its anchor. */
export interface PendingAssetSink {
  /** Register `work` as pending until it settles (either way); returns `work` unchanged. */
  track<T>(label: string, anchor: PendingAnchor, work: Promise<T>): Promise<T>;
  /** Register a callback-style load; call the returned function once it settles. */
  begin(label: string, anchor: PendingAnchor): () => void;
}

/** One pending load. `key` is unique per registration (labels repeat). */
export interface PendingEntry {
  readonly key: string;
  readonly label: string;
  readonly anchor: PendingAnchor;
}

/** The registry the loaders report to (one per Game). */
export class PendingAssets implements PendingAssetSink {
  private nextId = 0;
  private readonly live = new Map<number, PendingEntry>();
  /** Loads registered / settled since construction (diagnostics). */
  started = 0;
  settled = 0;

  begin(label: string, anchor: PendingAnchor): () => void {
    const id = this.nextId++;
    this.live.set(id, { key: `asset#${id}`, label, anchor });
    this.started += 1;
    return () => {
      if (this.live.delete(id)) this.settled += 1;
    };
  }

  track<T>(label: string, anchor: PendingAnchor, work: Promise<T>): Promise<T> {
    const done = this.begin(label, anchor);
    work.then(done, done);
    return work;
  }

  /** The loads still in flight. */
  entries(): PendingEntry[] {
    return [...this.live.values()];
  }
}
